import { useEffect, useState } from 'react'
import { ArrowUpToLine, Trash2, Wrench } from 'lucide-react'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { SubirEvidencias } from './SubirEvidencias'

/**
 * Qué pasa con la herramienta al cerrar una incidencia (ALM-01).
 *
 * Antes el cierre solo pedía una nota y ofrecía cambiar el estado de forma
 * opcional, así que se podía cerrar sin decidir nada: la herramienta se quedaba
 * en falla y no disponible de forma indefinida, hasta que alguien la editara a
 * mano. Ahora el desenlace es obligatorio.
 *
 * Emite el cuerpo listo para el API, o `null` mientras falte algo, para que
 * quien lo use bloquee su botón con `disabled={!desenlace}` en lugar de repetir
 * la validación. Las dos entradas al cierre —el detalle del reporte y el cierre
 * rápido del listado— comparten esta pieza.
 */

export type Desenlace =
  | { outcome: 'back_in_service'; toolStatus: string }
  | { outcome: 'to_maintenance'; maintenanceDestination: string }
  | {
      outcome: 'decommissioned'
      decommissionReason: string
      evidenceUrl1?: string
      evidenceUrl2?: string
      evidenceUrl3?: string
    }

type Clave = 'back_in_service' | 'to_maintenance' | 'decommissioned'

/** Igual que los campos del formulario de herramientas, para no verse aparte. */
const CLASE_CAMPO =
  'w-full px-3 py-2.5 rounded-lg border-0 bg-[#F6F9FB] text-sm text-gray-800 transition-colors focus:ring-2 focus:ring-blue-500'

const OPCIONES: { valor: Clave; titulo: string; detalle: string; icono: typeof Wrench }[] = [
  {
    valor: 'back_in_service',
    titulo: 'Vuelve a servir',
    detalle: 'Se reparó en sitio o el reporte fue un error. Queda lista para asignarse.',
    icono: ArrowUpToLine,
  },
  {
    valor: 'to_maintenance',
    titulo: 'Va a mantenimiento',
    detalle: 'Se manda al taller. Cuando regrese, el retorno define en qué estado queda.',
    icono: Wrench,
  },
  {
    valor: 'decommissioned',
    titulo: 'Se da de baja',
    detalle: 'Ya no sirve o no apareció. Sale del inventario de forma definitiva.',
    icono: Trash2,
  },
]

/** Estados utilizables: son los que dejan la herramienta lista para asignarse. */
const ESTADOS_QUE_SIRVEN = [
  { valor: 'good', etiqueta: 'En buen estado' },
  { valor: 'new_equipment', etiqueta: 'Equipo nuevo' },
]

const MOTIVOS_BAJA = [
  { valor: 'theft', etiqueta: 'Robo' },
  { valor: 'loss', etiqueta: 'Pérdida' },
  { valor: 'irreparable_damage', etiqueta: 'Daño irreparable' },
  { valor: 'end_of_life', etiqueta: 'Fin de vida útil' },
]

export function CamposDeDesenlace({
  onChange,
  onSubiendoChange,
  /**
   * Una herramienta faltante no tiene reparación: o apareció y vuelve al
   * inventario, o se da de baja por pérdida. Mandarla al taller no aplica.
   */
  esFaltante = false,
}: {
  onChange: (desenlace: Desenlace | null) => void
  onSubiendoChange?: (subiendo: boolean) => void
  esFaltante?: boolean
}) {
  const [clave, setClave] = useState<Clave | ''>('')
  const [estado, setEstado] = useState('')
  const [taller, setTaller] = useState('')
  const [motivo, setMotivo] = useState('')
  const [evidencias, setEvidencias] = useState<(string | null)[]>([null, null, null])

  const opciones = esFaltante
    ? OPCIONES.filter((o) => o.valor !== 'to_maintenance')
    : OPCIONES

  useEffect(() => {
    if (clave === 'back_in_service') {
      onChange(estado ? { outcome: clave, toolStatus: estado } : null)
      return
    }
    if (clave === 'to_maintenance') {
      onChange(taller.trim() ? { outcome: clave, maintenanceDestination: taller.trim() } : null)
      return
    }
    if (clave === 'decommissioned') {
      // Las vistas previas locales (blob:) no se mandan: solo cuentan las que ya
      // subieron y tienen URL del servidor.
      const subidas = evidencias.filter((u): u is string => !!u && !u.startsWith('blob:'))
      onChange(
        motivo
          ? {
              outcome: clave,
              decommissionReason: motivo,
              ...(subidas[0] && { evidenceUrl1: subidas[0] }),
              ...(subidas[1] && { evidenceUrl2: subidas[1] }),
              ...(subidas[2] && { evidenceUrl3: subidas[2] }),
            }
          : null,
      )
      return
    }
    onChange(null)
  }, [clave, estado, taller, motivo, evidencias, onChange])

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-medium text-gray-900">¿Qué pasa con la herramienta?</p>

      <div className="flex flex-col gap-2">
        {opciones.map(({ valor, titulo, detalle, icono: Icono }) => {
          const elegida = clave === valor
          return (
            <button
              key={valor}
              type="button"
              onClick={() => setClave(valor)}
              className={`flex items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
                elegida ? 'border-[#315FB9] bg-[#F4F7FB]' : 'border-[#E0E0E0] hover:bg-gray-50'
              }`}
            >
              <Icono
                className={`mt-0.5 h-5 w-5 flex-shrink-0 ${
                  elegida ? 'text-[#315FB9]' : 'text-gray-400'
                }`}
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-900">{titulo}</span>
                <span className="block text-xs text-gray-500">{detalle}</span>
              </span>
            </button>
          )
        })}
      </div>

      {clave === 'back_in_service' && (
        <label className="flex flex-col gap-1">
          <span className="text-sm font-medium text-gray-900">¿En qué estado queda?</span>
          <SelectBuscable
            variante="formulario"
            opciones={ESTADOS_QUE_SIRVEN.map((o) => ({ value: o.valor, label: o.etiqueta }))}
            value={estado}
            onChange={setEstado}
            placeholder="Selecciona el estado"
            placeholderDeBusqueda="Buscar..."
          />
        </label>
      )}

      {clave === 'to_maintenance' && (
        <label className="flex flex-col gap-1">
          <span className="text-sm font-medium text-gray-900">¿A qué taller o técnico?</span>
          <input
            value={taller}
            onChange={(e) => setTaller(e.target.value)}
            placeholder="Ej. Taller Herrera"
            className={CLASE_CAMPO}
          />
          <span className="text-xs text-gray-500">
            Se abre el mantenimiento a tu nombre con la fecha de hoy.
          </span>
        </label>
      )}

      {clave === 'decommissioned' && (
        <>
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium text-gray-900">Motivo de la baja</span>
            <SelectBuscable
              variante="formulario"
              opciones={MOTIVOS_BAJA.map((m) => ({ value: m.valor, label: m.etiqueta }))}
              value={motivo}
              onChange={setMotivo}
              placeholder="Selecciona el motivo"
              placeholderDeBusqueda="Buscar..."
            />
          </label>

          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-gray-900">Evidencia (opcional)</span>
            <SubirEvidencias
              urls={evidencias}
              onChange={setEvidencias}
              onSubiendoChange={onSubiendoChange}
            />
          </div>
        </>
      )}
    </div>
  )
}

export default CamposDeDesenlace
